import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { NotificationDto } from '@biyolink/shared';
import {
  useBadgeCounts,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
} from '@/api/notifications';
import { formatDateTime } from '@/lib/format';
import { cn } from '@/lib/cn';
import { t } from '@/i18n';

/** Açılır listede gösterilen bildirim sayısı (docs/07 Faz 4: son 10). */
const DROPDOWN_LIMIT = 10;

/** Header'daki bildirim zili + açılır liste. */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const badges = useBadgeCounts();
  // Liste yalnızca zil açıkken çekilir; kapalıyken sayaç yoklaması yeterli.
  const notifications = useNotifications({ page: 1 });
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const unread = badges.data?.notifications ?? 0;

  useEffect(() => {
    if (!open) return;

    function handleClickOutside(event: MouseEvent) {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') setOpen(false);
    }

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [open]);

  function openNotification(notification: NotificationDto) {
    setOpen(false);
    if (notification.readAt === null) markRead.mutate(notification.id);
    if (notification.linkPath) navigate(notification.linkPath);
  }

  const items = (notifications.data?.items ?? []).slice(0, DROPDOWN_LIMIT);

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        aria-label={t('notifications.bellLabel')}
        className="relative rounded-lg p-2 text-slate-600 transition hover:bg-slate-100"
      >
        <span aria-hidden="true" className="block text-lg leading-none">
          🔔
        </span>
        {unread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 grid h-5 min-w-5 place-items-center rounded-full bg-red-600 px-1 text-[11px] font-semibold text-white">
            {unread > 99 ? '99+' : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-[min(22rem,calc(100vw-2rem))] overflow-hidden rounded-card border border-slate-200 bg-surface shadow-xl">
          <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2.5">
            <p className="text-sm font-semibold text-slate-900">{t('notifications.title')}</p>
            {unread > 0 && (
              <button
                type="button"
                onClick={() => markAllRead.mutate()}
                className="text-xs font-medium text-primary-900 hover:underline"
              >
                {t('notifications.markAllRead')}
              </button>
            )}
          </div>

          <ul className="max-h-80 divide-y divide-slate-100 overflow-y-auto">
            {items.length === 0 && (
              <li className="px-4 py-8 text-center text-sm text-slate-500">
                {t('notifications.emptyTitle')}
              </li>
            )}
            {items.map((notification) => (
              <li key={notification.id}>
                <button
                  type="button"
                  onClick={() => openNotification(notification)}
                  className={cn(
                    'block w-full px-4 py-3 text-left transition hover:bg-slate-50',
                    notification.readAt === null && 'bg-primary-50/60',
                  )}
                >
                  <p
                    className={cn(
                      'text-sm',
                      notification.readAt === null
                        ? 'font-semibold text-slate-900'
                        : 'text-slate-700',
                    )}
                  >
                    {notification.title}
                  </p>
                  {notification.body && (
                    <p className="mt-0.5 text-xs text-slate-600">{notification.body}</p>
                  )}
                  <p className="mt-1 text-[11px] text-slate-400">
                    {formatDateTime(notification.createdAt)}
                  </p>
                </button>
              </li>
            ))}
          </ul>

          <div className="border-t border-slate-200 px-4 py-2.5 text-center">
            <Link
              to="/panel/bildirimler"
              onClick={() => setOpen(false)}
              className="text-sm font-medium text-primary-900 hover:underline"
            >
              {t('notifications.viewAll')}
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
